import { useEffect, useState } from 'react';
import {
  bulkUpdateAllowEmailChange as bulkUpdateAllowEmailChangeRequest,
  changeUserRole as changeUserRoleRequest,
  getAllRoles,
  changeEmail
} from 'src/services/client';
import { toast } from 'react-toastify';
import { UserRole } from 'src/SharedCustomHooks/UseSuiteActions/types';
import { IChangeContactEmailPayload } from 'src/pages/Contacts/apiContracts';

interface UseUserManagementAPIState {
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
}

export interface BulkUpdateAllowEmailChangeResult {
  ok: boolean;
  updatedCount: number;
}

export interface UseUserManagementAPIReturn {
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
  allRoles: UserRole[];
  isRolesLoading: boolean;
  changeUserRole: (userId: string, role: string) => Promise<boolean>;
  bulkUpdateAllowEmailChange: (
    userIds: string[],
    allow: boolean
  ) => Promise<BulkUpdateAllowEmailChangeResult>;
  getAllUserTypeRoles: () => Promise<UserRole[]>;
  changeUserEmail: (payload: IChangeContactEmailPayload) => Promise<boolean>;
  clearError: () => void;
  clearSuccess: () => void;
}

const DEFAULT_ERROR_BY_STATUS: Record<number, string> = {
  400: 'This role change is not allowed.',
  403: 'You do not have permission to change this user\'s role.',
  404: 'User not found.',
  500: 'Something went wrong on our end. Please try again.',
};

const DEFAULT_EMAIL_CHANGE_ERROR_BY_STATUS: Record<number, string> = {
  400: 'That email can\'t be used. Please check and try again.',
  403: 'You do not have permission to change this contact\'s email.',
  404: 'User not found.',
  500: 'Something went wrong on our end. Please try again.',
};

const DEFAULT_BULK_EMAIL_ERROR_BY_STATUS: Record<number, string> = {
  400: 'No contacts were selected.',
  403: 'You do not have permission to update email settings.',
  404: 'None of the selected contacts could be found.',
  500: 'Something went wrong on our end. Please try again.',
};

const extractEnvelopeMessage = async (
  response: Response
): Promise<string | null> => {
  try {
    const body: any = await response.json();
    return body?.message ?? null;
  } catch {
    return null;
  }
};

/* Shared data-fetching hook for the UserManagement API surface. Extend with additional methods and state slices as the
 * API grows; component-level orchestrator hooks compose this into facades tailored to each UI. */
export const useUserManagementAPI = (): UseUserManagementAPIReturn => {
  const [state, setState] = useState<UseUserManagementAPIState>({
    isSubmitting: false,
    error: null,
    successMessage: null,
  });

  const [allRoles, setAllRoles] = useState<UserRole[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState<boolean>(false);


 const changeUserEmail = async (
    payload: IChangeContactEmailPayload
  ): Promise<boolean> => {
    setState({ isSubmitting: true, error: null, successMessage: null });

    try {
      const response: any = await changeEmail(payload);

      if (response.ok || response.status === 200) {
        const message: string | null = await extractEnvelopeMessage(response);

        const newState = {
          isSubmitting: false,
          error: null,
          successMessage: message ?? 'Verification email sent.',
        };

        setState(newState);

        toast.success(
          ` ⓘ A verification email was sent to the provided email address.`,
          { autoClose: 6000, position: 'top-center' }
        );

        return true;
      }

      const serverMessage: string | null = await extractEnvelopeMessage(response);

      const fallback: string =
        DEFAULT_EMAIL_CHANGE_ERROR_BY_STATUS[response.status] ??
        `Email change failed. (Status ${response.status})`;

      const newState = {
        isSubmitting: false,
        error: serverMessage ?? fallback,
        successMessage: null,
      };

      setState(newState);

      return false;
    } catch (err: any) {
      setState({
        isSubmitting: false,
        error: err?.message ?? 'Something went wrong while changing the email.',
        successMessage: null,
      });

      return false;
    }
  };

  const changeUserRole = async (
    userId: string,
    role: string
  ): Promise<boolean> => {
    setState({ isSubmitting: true, error: null, successMessage: null });

    try {
      const response: any = await changeUserRoleRequest(userId, role);

      if (response.ok || response.status === 200) {
        const message: string | null = await extractEnvelopeMessage(response);

        const newState = {
          isSubmitting: false,
          error: null,
          successMessage: message ?? 'User role changed.',
        };

        setState(newState);

        toast.success(
          ` ⓘ Your User Role has been updated to ${role}.`,
          { autoClose: 6000, position: 'top-center' }
        );

        return true;
      }

      const serverMessage: string | null = await extractEnvelopeMessage(response);

      const fallback: string =
        DEFAULT_ERROR_BY_STATUS[response.status] ??
        `Role change failed. (Status ${response.status})`;

      const newState = {
        isSubmitting: false,
        error: serverMessage ?? fallback,
        successMessage: null,
      };

      setState(newState);

      return false;
    } catch (err: any) {
      setState({
        isSubmitting: false,
        error: err?.message ?? 'Something went wrong while changing the role.',
        successMessage: null,
      });

      return false;
    }
  };

  const bulkUpdateAllowEmailChange = async (
    userIds: string[],
    allow: boolean
  ): Promise<BulkUpdateAllowEmailChangeResult> => {
    setState({ isSubmitting: true, error: null, successMessage: null });

    try {
      const response: any = await bulkUpdateAllowEmailChangeRequest(
        userIds,
        allow
      );

      if (response.ok || response.status === 200) {
        const body: any = await response.json().catch(() => ({}));
        const updatedCount: number =
          typeof body?.updated === 'number' ? body.updated : userIds.length;

        const successMessage: string = `Updated ${updatedCount} contact${
          updatedCount === 1 ? '' : 's'
        }.`;

        setState({
          isSubmitting: false,
          error: null,
          successMessage,
        });

        return { ok: true, updatedCount };
      }

      const serverMessage: string | null =
        await extractEnvelopeMessage(response);

      const fallback: string =
        DEFAULT_BULK_EMAIL_ERROR_BY_STATUS[response.status] ??
        `Update failed. (Status ${response.status})`;

      setState({
        isSubmitting: false,
        error: serverMessage ?? fallback,
        successMessage: null,
      });

      return { ok: false, updatedCount: 0 };
    } catch (err: any) {
      setState({
        isSubmitting: false,
        error:
          err?.message ??
          'Something went wrong while updating email settings.',
        successMessage: null,
      });

      return { ok: false, updatedCount: 0 };
    }
  };

  /* Fetch all user-type roles. Updates the hook's `allRoles` cache AND returns
   * the fresh list so callers that want to refetch post-success can also read
   * the updated value inline. Errors log and surface as []. */
  const getAllUserTypeRoles = async (): Promise<UserRole[]> => {
    setIsRolesLoading(true);

    try {
      const response: any = await getAllRoles();

      if (!response.ok && response.status !== 200) {
        throw new Error(`Failed to fetch user roles: ${response.status}`);
      }

      const result: any = await response.json();
      const roles: UserRole[] = Array.isArray(result)
        ? result
        : result?.data ?? [];

      setAllRoles(roles);
      return roles;
    } catch (err: any) {
      console.error('getAllUserTypeRoles error:', err);
      setAllRoles([]);
      return [];
    } finally {
      setIsRolesLoading(false);
    }
  };

  useEffect(() => {
    void getAllUserTypeRoles();
  }, []);

  const clearError = () => {
    setState((prev: UseUserManagementAPIState) => ({ ...prev, error: null }));
  };

  const clearSuccess = () => {
    setState((prev: UseUserManagementAPIState) => ({
      ...prev,
      successMessage: null,
    }));
  };

  return {
    isSubmitting: state.isSubmitting,
    error: state.error,
    successMessage: state.successMessage,
    allRoles,
    isRolesLoading,
    changeUserRole,
    bulkUpdateAllowEmailChange,
    getAllUserTypeRoles,
    clearError,
    clearSuccess,
    changeUserEmail,
  };
};
